import { MouseEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import CallIcon from "@mui/icons-material/Call";
import LaunchIcon from "@mui/icons-material/Launch";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import debounce from "lodash/debounce";

import { misc, neutral, success } from "app/themes/colors";
import { normalizePhoneNumber } from "components/suno-dialer/utils";
import { CRM_SEGMENTS, PHONE_TYPES } from "constants/patients";
import ContactAvatar from "features/calls/rolodex/contact-avatar.component";
import {
  contactPath,
  contactSubtitle
} from "features/calls/rolodex/contact-kind.util";
import useContactPrivacySx, {
  ContactPrivacySx
} from "features/calls/rolodex/use-contact-privacy.hook";
import { DialstackContact, useGetContactsQuery } from "services/dialstack";
import {
  DEFAULT_PATIENTS_QUERY,
  useQueryPatientsQuery
} from "services/patients";
import type { Patient } from "services/types/patients.type";
import { formatPhoneNumber } from "utils/common";

import { PLUM_600 } from "./dialstack-softphone-widget.styles";
import {
  toOutboundCallPatient,
  useDialstackSoftphone
} from "./use-dialstack-softphone.hook";

const SEARCH_MIN_CHARS = 2;
const SEARCH_LIMIT = 5;
const SPEED_DIAL_LIMIT = 20;

type SoftphoneSearchProps = {
  // Called after a number is dialed so the widget can flip back to the SDK
  // view, where the in-call controls live.
  onDialed: () => void;
};

type PhoneEntry = {
  key: string;
  label: string;
  number: string;
};

// The patient's dialable numbers with the old dialer's labels
// ("Primary (Mobile):", "Secondary (Landline):").
function phoneEntries(patient: Patient): PhoneEntry[] {
  const entries: PhoneEntry[] = [];
  if (patient.phone) {
    const type = PHONE_TYPES.getFromCode(patient.phone_type);
    entries.push({
      key: "primary",
      label: type ? `Primary (${type}):` : "Primary:",
      number: patient.phone
    });
  }
  if (patient.secondary_phone) {
    const type = PHONE_TYPES.getFromCode(patient.secondary_phone_type);
    entries.push({
      key: "secondary",
      label: type ? `Secondary (${type}):` : "Secondary:",
      number: patient.secondary_phone
    });
  }
  return entries;
}

const resultCardSx = {
  p: 1.5,
  borderRadius: "14px",
  bgcolor: "rgba(255, 255, 255, 0.96)",
  boxShadow: "0 6px 14px -8px hsla(271, 30%, 15%, 0.5)"
} as const;

const dialButtonSx = {
  color: "#FFFFFF",
  bgcolor: success[600],
  "&:hover": { bgcolor: success[700] }
} as const;

function NumberRow({
  label,
  number,
  onDial,
  privacySx
}: {
  label: string;
  number: string;
  onDial: () => void;
  // A family contact's number blurs in privacy mode.
  privacySx?: ContactPrivacySx;
}) {
  const formatted = formatPhoneNumber(number) || number;
  return (
    <Stack direction="row" alignItems="center" sx={{ mt: 0.5 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 700, ...privacySx }}>
          {formatted}
        </Typography>
      </Box>
      <Tooltip disableInteractive title={`Call ${formatted}`}>
        <IconButton
          aria-label={`Call ${formatted}`}
          onClick={onDial}
          sx={dialButtonSx}
        >
          <CallIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function PatientResultCard({
  patient,
  onDial
}: {
  patient: Patient;
  onDial: (phoneNumber: string, patient: Patient) => void;
}) {
  const navigate = useNavigate();
  const href = `/patients/${patient.id}`;
  const segment = CRM_SEGMENTS.getFromCode(patient.crm_segment);
  const entries = phoneEntries(patient);

  const handleNameClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigate(href);
  };

  return (
    <Box
      data-testid={`softphone-patient-result-${patient.id}`}
      sx={resultCardSx}
    >
      <Stack direction="row" spacing={1.5}>
        <Avatar src={patient.photo || undefined} sx={{ width: 44, height: 44 }}>
          {(patient.first_name?.[0] ?? "") + (patient.last_name?.[0] ?? "")}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Link
            href={href}
            onClick={handleNameClick}
            underline="hover"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: 15,
              fontWeight: 700,
              color: PLUM_600
            }}
          >
            {patient.fullName ||
              `${patient.first_name ?? ""} ${patient.last_name ?? ""}`.trim()}
            <LaunchIcon sx={{ fontSize: 14 }} />
          </Link>
          <Typography sx={{ fontSize: 14, color: neutral[800] }}>
            {[
              segment,
              patient.age_years ? `${patient.age_years} years old` : null
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
          {entries.map((entry) => (
            <NumberRow
              key={entry.key}
              label={entry.label}
              number={entry.number}
              onDial={() => onDial(entry.number, patient)}
            />
          ))}
          {entries.length === 0 && (
            <Typography sx={{ fontSize: 12, color: neutral[600] }}>
              No phone number on file
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

// A Rolodex contact (ENG-2177): every number dials; the name opens the
// contact. A family contact blurs in privacy mode like a patient would.
function ContactResultCard({
  contact,
  onDial
}: {
  contact: DialstackContact;
  onDial: (phoneNumber: string) => void;
}) {
  const navigate = useNavigate();
  const privacySx = useContactPrivacySx(contact.kind);
  const href = contactPath(contact.id);
  const handleNameClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigate(href);
  };
  return (
    <Box
      data-testid={`softphone-contact-result-${contact.id}`}
      sx={resultCardSx}
    >
      <Stack direction="row" spacing={1.5}>
        <ContactAvatar contact={contact} size={44} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Link
            href={href}
            onClick={handleNameClick}
            underline="hover"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: 15,
              fontWeight: 700,
              color: PLUM_600
            }}
          >
            <Box component="span" sx={privacySx}>
              {contact.name}
            </Box>
            <LaunchIcon sx={{ fontSize: 14 }} />
          </Link>
          <Typography sx={{ fontSize: 14, color: neutral[800], ...privacySx }}>
            {contactSubtitle(contact)}
          </Typography>
          {contact.phone_numbers.map((number, index) => (
            <NumberRow
              key={number.id}
              label={`${number.label || (index === 0 ? "Primary" : "Phone")}:`}
              number={number.number}
              onDial={() => onDial(number.number)}
              privacySx={privacySx}
            />
          ))}
          {contact.phone_numbers.length === 0 && (
            <Typography sx={{ fontSize: 12, color: neutral[600] }}>
              No phone number on file
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

function GroupLabel({
  children,
  icon,
  testId
}: {
  children: string;
  icon?: React.ReactNode;
  testId: string;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 0.5 }}>
      {icon}
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.06em",
          color: "rgba(255, 255, 255, 0.85)"
        }}
        data-testid={testId}
      >
        {children.toUpperCase()}
      </Typography>
    </Stack>
  );
}

function Hint({ children, testId }: { children: string; testId?: string }) {
  return (
    <Typography
      sx={{
        fontSize: 13,
        color: "rgba(255, 255, 255, 0.78)",
        textAlign: "center",
        py: 2
      }}
      data-testid={testId}
    >
      {children}
    </Typography>
  );
}

// The old suno-comms dialer's "search patients" tab, rebuilt on top of the
// DialStack softphone: search by name, dial straight from the card. The
// results come in two groups — Patients and Rolodex contacts (ENG-2177) —
// and before typing the user's own speed dial is offered.
export default function SoftphoneSearch({ onDialed }: SoftphoneSearchProps) {
  const { dialNumber, selectedCallerId } = useDialstackSoftphone();
  const [inputValue, setInputValue] = useState("");
  const [search, setSearch] = useState("");

  const debouncedSetSearch = useMemo(() => debounce(setSearch, 250), []);
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const isSearchable = search.trim().length >= SEARCH_MIN_CHARS;
  const { data, isFetching } = useQueryPatientsQuery(
    {
      search: search.trim(),
      baseFilter: {},
      is_active: true,
      limit: SEARCH_LIMIT,
      query: DEFAULT_PATIENTS_QUERY
    },
    { skip: !isSearchable }
  );
  const { data: contactData, isFetching: isFetchingContacts } =
    useGetContactsQuery(
      { search: search.trim(), ordering: "name", limit: SEARCH_LIMIT },
      { skip: !isSearchable }
    );
  const { data: speedDialData } = useGetContactsQuery({
    favorite: true,
    ordering: "name",
    limit: SPEED_DIAL_LIMIT
  });

  // The widget's "Call from" pick, if any, applies to this dial (ENG-1938).
  const dialOptions = { callerId: selectedCallerId };

  const handleDial = (phoneNumber: string, patient: Patient) => {
    dialNumber(
      `+1${normalizePhoneNumber(phoneNumber)}`,
      toOutboundCallPatient(patient),
      dialOptions
    );
    onDialed();
  };

  const handleDialContact = (phoneNumber: string) => {
    dialNumber(
      `+1${normalizePhoneNumber(phoneNumber)}`,
      undefined,
      dialOptions
    );
    onDialed();
  };

  const patients = isSearchable ? (data ?? []) : [];
  const contacts = isSearchable ? (contactData ?? []) : [];
  const speedDial = speedDialData ?? [];
  const isBusy = isFetching || isFetchingContacts;
  const noResults =
    !isBusy && isSearchable && patients.length === 0 && contacts.length === 0;

  return (
    <Box sx={{ p: 1.5, maxHeight: 420, overflowY: "auto" }}>
      <TextField
        fullWidth
        autoFocus
        size="small"
        placeholder="Search patients and contacts"
        value={inputValue}
        onChange={(event) => {
          setInputValue(event.target.value);
          debouncedSetSearch(event.target.value);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: neutral[600] }} />
            </InputAdornment>
          )
        }}
        sx={{
          bgcolor: "common.white",
          borderRadius: "12px",
          "& .MuiOutlinedInput-root": { borderRadius: "12px" }
        }}
      />
      <Stack spacing={1.25} sx={{ mt: 1.5 }}>
        {isBusy && (
          <>
            <Skeleton
              variant="rounded"
              height={88}
              sx={{ borderRadius: "14px", bgcolor: "rgba(255,255,255,0.35)" }}
            />
            <Skeleton
              variant="rounded"
              height={88}
              sx={{ borderRadius: "14px", bgcolor: "rgba(255,255,255,0.25)" }}
            />
          </>
        )}
        {!isBusy && patients.length > 0 && (
          <GroupLabel testId="softphone-search-group-patients">
            Patients
          </GroupLabel>
        )}
        {!isBusy &&
          patients.map((patient) => (
            <PatientResultCard
              key={patient.id}
              patient={patient}
              onDial={handleDial}
            />
          ))}
        {!isBusy && contacts.length > 0 && (
          <GroupLabel testId="softphone-search-group-contacts">
            Contacts
          </GroupLabel>
        )}
        {!isBusy &&
          contacts.map((contact) => (
            <ContactResultCard
              key={contact.id}
              contact={contact}
              onDial={handleDialContact}
            />
          ))}
        {noResults && (
          <Hint testId="softphone-search-empty">
            No patients or contacts found
          </Hint>
        )}
        {!isSearchable && speedDial.length > 0 && (
          <>
            <GroupLabel
              icon={<StarIcon sx={{ fontSize: 13, color: misc.starGold }} />}
              testId="softphone-search-group-speed-dial"
            >
              My speed dial
            </GroupLabel>
            {speedDial.map((contact) => (
              <ContactResultCard
                key={contact.id}
                contact={contact}
                onDial={handleDialContact}
              />
            ))}
          </>
        )}
        {!isSearchable && speedDial.length === 0 && (
          <Hint>Type a name to find a patient or contact</Hint>
        )}
      </Stack>
    </Box>
  );
}
